import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { flatMap } from "lodash";
import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";

import type { FlaggedContentReport } from "@/react/types/FlaggedContent";

const PER_PAGE = 10;

interface MemberConnectionReportsResponse {
  count: number;
  has_next_page: boolean;
  page: number;
  per_page: number;
  records: FlaggedContentReport[];
}

const fetchReports = (id: string, page: number) =>
  reactQueryGet<MemberConnectionReportsResponse>(
    internalApi.flaggedContent.memberConnectionReports({
      id,
      params: { page, per_page: PER_PAGE },
    }),
  );

export const useMemberConnectionReports = (flaggedContentId?: string) => {
  const query = useInfiniteQuery({
    queryKey: ["member-connection-reports", flaggedContentId],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      fetchReports(flaggedContentId ?? "", pageParam),
    getNextPageParam: lastPage =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    enabled: !!flaggedContentId,
  });

  const reports = useMemo(
    () => flatMap(query.data?.pages ?? [], page => page.records),
    [query.data?.pages],
  );

  const totalCount = query.data?.pages[0]?.count ?? 0;

  return {
    reports,
    totalCount,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isLoading: query.isInitialLoading,
  };
};
